import { Button } from "@/components/ui/button";
import { useEventHook, EventHookConfig } from "@/hooks/useEventHook";
import { Loader2 } from "lucide-react";
import { cn } from "@/utils/utils";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useRef } from "react";
import { buildActionButtonUrl } from "./actionButtonUrl";

export type ButtonFieldMode = "post" | "new_tab" | "popup" | "post_then_open";
export type ButtonFieldOpenTarget = "new_tab" | "popup";

export interface ButtonFieldConfig extends EventHookConfig {
  variant?: "primary" | "secondary" | "tertiary";
  mode?: ButtonFieldMode;
  /** Link template; `{{field_key}}` placeholders are filled from the record. */
  url?: string;
  /** Where `post_then_open` opens the link once the send succeeds. */
  open_target?: ButtonFieldOpenTarget;
  popup_width?: number;
  popup_height?: number;
}

export interface ButtonFieldProps {
  config: ButtonFieldConfig;
  entityId: string;
  entity?: Record<string, unknown>;
  label?: string;
  className?: string;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

const BUTTON_FIELD_MODES: readonly ButtonFieldMode[] = [
  "post",
  "new_tab",
  "popup",
  "post_then_open"
];

// The config comes from the API untyped, so anything but a known mode keeps
// the original send-only behaviour.
const resolveMode = (mode: unknown): ButtonFieldMode =>
  BUTTON_FIELD_MODES.includes(mode as ButtonFieldMode)
    ? (mode as ButtonFieldMode)
    : "post";

const DEFAULT_POPUP_WIDTH = 800;
const DEFAULT_POPUP_HEIGHT = 600;
// Long enough to reach the manual link: retrying the button instead would
// repeat the send in send-then-open mode.
const BLOCKED_TOAST_DURATION = 10000;

const popupDimension = (value: number | undefined, fallback: number) =>
  typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.round(value)
    : fallback;

const popupFeatures = (config: ButtonFieldConfig) => {
  const width = popupDimension(config.popup_width, DEFAULT_POPUP_WIDTH);
  const height = popupDimension(config.popup_height, DEFAULT_POPUP_HEIGHT);
  const left = Math.max(
    0,
    Math.round(window.screenX + (window.outerWidth - width) / 2)
  );
  const top = Math.max(
    0,
    Math.round(window.screenY + (window.outerHeight - height) / 2)
  );
  return `popup=yes,width=${width},height=${height},left=${left},top=${top}`;
};

const ButtonField = ({
  config,
  entityId,
  entity,
  label,
  className,
  onSuccess,
  onError
}: ButtonFieldProps) => {
  const { t } = useTranslation();
  const buttonLabel = config.button_label || label || "Execute";
  const mode = resolveMode(config.mode);

  const openLink = (url: string, target: ButtonFieldOpenTarget) => {
    // Opened without `noopener` so a blocked window is detectable (with it,
    // window.open always returns null); the opener is cut right after instead.
    const opened =
      target === "popup"
        ? window.open(url, "_blank", popupFeatures(config))
        : window.open(url, "_blank");
    if (opened) {
      opened.opener = null;
      return;
    }
    toast.error(
      <span>
        {t("actionButton.windowBlocked")}{" "}
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          {t("actionButton.openLinkManually")}
        </a>
      </span>,
      { duration: BLOCKED_TOAST_DURATION }
    );
  };

  const resolveLink = (): string | null => {
    const template = typeof config.url === "string" ? config.url : "";
    const result = buildActionButtonUrl(template, {
      ...entity,
      id: entity?.id ?? entityId
    });
    if (result.ok) return result.url;
    toast.error(
      result.reason === "missing_value"
        ? t("actionButton.missingValue")
        : t("actionButton.invalidLink")
    );
    return null;
  };

  // Resolved at click time so a send-then-open button never sends when its
  // link could not be opened afterwards.
  const pendingUrl = useRef<string | null>(null);

  const { execute, isLoading, loadingText } = useEventHook(config, {
    onSuccess: (data) => {
      toast.success(`${buttonLabel} completed successfully`);
      const urlToOpen = pendingUrl.current;
      pendingUrl.current = null;
      if (mode === "post_then_open" && urlToOpen) {
        openLink(urlToOpen, config.open_target || "new_tab");
      }
      onSuccess?.(data);
    },
    onError: (error) => {
      pendingUrl.current = null;
      toast.error(`Failed to complete ${buttonLabel}`);
      onError?.(error);
    }
  });

  const handleClick = () => {
    if (mode === "post") {
      pendingUrl.current = null;
      execute(entityId);
      return;
    }
    const url = resolveLink();
    if (!url) return;
    if (mode === "post_then_open") {
      pendingUrl.current = url;
      execute(entityId);
      return;
    }
    openLink(url, mode);
  };

  return (
    <Button
      variant={config.variant || "secondary"}
      onClick={handleClick}
      disabled={isLoading}
      className={cn("min-w-[120px]", className)}
    >
      {isLoading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {loadingText}
        </>
      ) : (
        buttonLabel
      )}
    </Button>
  );
};

export default ButtonField;
